/**
 * Eric (2026-06-16 — iter106 SPEC STRICK) — Page de status PUBLIQUE pour
 * que Vincent / JS / clients puissent vérifier eux-mêmes l'état de Knoria.
 * Sans auth. Auto-refresh toutes les 30 secondes.
 *
 * Source de données : GET /api/status (backend, no-auth)
 * Indicateurs :
 *   • Backend FastAPI
 *   • MongoDB (ping)
 *   • Heartbeat (worker interne — devrait écrire toutes les 30s)
 *   • Incidents auto-détectés dans les 24 dernières heures
 */
import React, { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const StatusBadge = ({ ok, label }) => (
  <span
    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${
      ok ? "bg-teal-100 text-teal-700 border border-teal-300"
         : "bg-rose-100 text-rose-700 border border-rose-300"
    }`}
    data-testid={`status-badge-${ok ? "ok" : "ko"}`}
  >
    <span className={`w-2 h-2 rounded-full ${ok ? "bg-teal-500 animate-pulse" : "bg-rose-500"}`} />
    {ok ? "✓ " + label : "✗ " + label}
  </span>
);

export default function StatusPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [refreshedAt, setRefreshedAt] = useState(null);

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/api/status`, { timeout: 10000 });
      setData(r.data);
      setError(null);
      setRefreshedAt(new Date());
    } catch (e) {
      setError(e?.message || "Impossible de joindre le backend");
      setData(null);
    }
  };

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 30000);
    return () => clearInterval(id);
  }, []);

  const overallOk = data?.ok && !error;
  const isLoading = !data && !error;

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-50 to-zinc-100 p-6 lg:p-12" data-testid="status-page">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl lg:text-4xl font-bold text-zinc-900 mb-2">État de Knoria</h1>
          <p className="text-sm text-zinc-500">Vérifié toutes les 30 secondes — sans authentification</p>
        </div>

        <div
          className={`rounded-2xl border-2 p-6 mb-6 transition-all ${
            isLoading
              ? "bg-zinc-100 border-zinc-300"
              : overallOk
                ? "bg-teal-50 border-teal-300"
                : "bg-rose-50 border-rose-300"
          }`}
          data-testid="status-overall"
        >
          <div className="flex items-center gap-4">
            <span className={`text-5xl ${overallOk || isLoading ? "" : "animate-bounce"}`}>
              {isLoading ? "⏳" : overallOk ? "✅" : "🔴"}
            </span>
            <div>
              <h2 className="text-xl font-bold text-zinc-900" data-testid="status-headline">
                {isLoading
                  ? "Vérification en cours…"
                  : overallOk
                    ? "Tous les systèmes sont opérationnels"
                    : "Une anomalie est détectée"}
              </h2>
              <p className="text-xs text-zinc-600 mt-1">
                {refreshedAt ? `Dernière vérification : ${refreshedAt.toLocaleTimeString("fr-CA")}` : "Initialisation…"}
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-rose-50 border border-rose-300 rounded-xl p-4 mb-6" data-testid="status-error">
            <p className="text-sm text-rose-800 font-semibold">⚠️ Le frontend ne joint pas le backend</p>
            <p className="text-xs text-rose-700 mt-1">{error}</p>
          </div>
        )}

        {data?.components && (
          <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden" data-testid="status-components">
            <div className="px-5 py-3 border-b border-zinc-200 bg-zinc-50">
              <h3 className="text-sm font-bold text-zinc-700 uppercase tracking-wide">Composants</h3>
            </div>
            <div className="divide-y divide-zinc-100">
              {Object.entries(data.components).map(([name, c]) => (
                <div key={name} className="flex items-center justify-between gap-4 px-5 py-4" data-testid={`status-component-${name}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-zinc-900 capitalize">{name.replace(/_/g, " ")}</div>
                    <div className="text-xs text-zinc-500 mt-0.5 truncate" title={c.message}>{c.message || "—"}</div>
                  </div>
                  <StatusBadge ok={c.ok} label={c.ok ? "OK" : "KO"} />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8 text-center text-xs text-zinc-500">
          <p>Page publique — partageable avec Vincent, Jean-Sébastien, ou tout client.</p>
          <p className="mt-1">URL stable : <code className="bg-zinc-200 px-2 py-0.5 rounded">https://knoria.com/status</code></p>
        </div>
      </div>
    </div>
  );
}
